import {useCallback, useEffect, useState} from "react";
import {useDispatch} from "react-redux";

import {BuybackEMIRecordService} from "../../../../services";
import {showErrorToast, showSuccessToast} from "../../../../state/toasts";
import type {BuybackEMIRecord, SaveBuybackEMIRecordPayload} from "../types";

/** Minimum ticket shape consumed by this hook. */
export interface BuybackEMITicketShape {
  /** Hyperlinked ticket URL (used to attach a fresh record). */
  url: string;
  /** Reference number — the lookup key for fetching the latest record. */
  reference_number: string;
}

interface PaginatedBuybackEMIResponse {
  results?: BuybackEMIRecord[];
}

export interface UseBuybackEMIReturn {
  record: BuybackEMIRecord | null;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  /** POST on first save, PATCH on subsequent saves. */
  save: (
    payload: SaveBuybackEMIRecordPayload,
  ) => Promise<BuybackEMIRecord | null>;
  /** DELETE the current record; returns ``true`` on success. */
  remove: () => Promise<boolean>;
}

/**
 * Loads + persists the most-recent Buyback / EMI record for a ticket.
 *
 * One logical capture per ticket: the first save creates the record, every
 * subsequent save PATCHes the same row so we don't accumulate duplicates.
 */
export const useBuybackEMI = (
  ticket: BuybackEMITicketShape | null | undefined,
): UseBuybackEMIReturn => {
  const dispatch = useDispatch();
  const [record, setRecord] = useState<BuybackEMIRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const reference = ticket?.reference_number;
    if (!reference) return undefined;

    let cancelled = false;
    setIsLoading(true);
    new BuybackEMIRecordService()
      .makeGetRequest<PaginatedBuybackEMIResponse>({
        ticket__reference_number: reference,
        ordering: "-created_at",
        page_size: 1,
      })
      .then((res) => {
        if (cancelled) return;
        // axios instance already unwraps to response.data (see helpers/backend.ts)
        setRecord(res?.results?.[0] ?? null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        dispatch(
          showErrorToast(
            "Error",
            err?.response?.data || "Failed to load Buyback / EMI record",
          ),
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticket?.reference_number, dispatch]);

  const save = useCallback(
    async (
      payload: SaveBuybackEMIRecordPayload,
    ): Promise<BuybackEMIRecord | null> => {
      if (!ticket?.url) return null;
      setIsSaving(true);
      try {
        let saved: BuybackEMIRecord;
        if (record?.url) {
          // Existing record — PATCH so we only send changed fields.
          const res = await new BuybackEMIRecordService(
            record.url,
          ).makePatchRequest<BuybackEMIRecord>(payload);
          saved = res;
        } else {
          // First capture — POST with the ticket hyperlink.
          const res = await new BuybackEMIRecordService().makePostRequest<BuybackEMIRecord>(
            {...payload, ticket: ticket.url},
          );
          saved = res;
        }
        setRecord(saved);
        dispatch(showSuccessToast("Success", "Buyback / EMI record saved."));
        return saved;
      } catch (err: any) {
        dispatch(
          showErrorToast(
            "Error",
            err?.response?.data || "Failed to save Buyback / EMI record",
          ),
        );
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [record?.url, ticket?.url, dispatch],
  );

  const remove = useCallback(async (): Promise<boolean> => {
    if (!record?.url) return false;
    setIsDeleting(true);
    try {
      await new BuybackEMIRecordService(record.url).makeDeleteRequest();
      setRecord(null);
      dispatch(showSuccessToast("Success", "Buyback / EMI record deleted."));
      return true;
    } catch (err: any) {
      dispatch(
        showErrorToast(
          "Error",
          err?.response?.data || "Failed to delete Buyback / EMI record",
        ),
      );
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [record?.url, dispatch]);

  return {record, isLoading, isSaving, isDeleting, save, remove};
};
